import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'

// Mobile-first button primitive — usersmatch-owned (widgets are per-app). Styled
// from the shared @upadelmatch/tokens via CSS vars; touch target ≥ --tap.
const button = cva(
  'inline-flex items-center justify-center gap-2 font-semibold select-none ' +
    'transition-colors duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ' +
    'rounded-[var(--radius)] min-h-[var(--tap)]',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--accent)] text-[color:var(--accent-fg)] hover:bg-[var(--accent-hi)]',
        outline:
          'border border-[var(--accent)] text-[color:var(--accent)] bg-transparent hover:bg-[var(--accent-wash)]',
        ghost: 'text-[color:var(--fg)] bg-transparent hover:bg-[var(--line-soft)]',
      },
      size: {
        md: 'px-4 text-[length:var(--fs-md)]',
        lg: 'px-6 text-[length:var(--fs-lg)] w-full',
        pill: 'px-5 rounded-full text-[length:var(--fs-md)]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type, ...props },
  ref,
) {
  return (
    <button ref={ref} type={type ?? 'button'} className={clsx(button({ variant, size }), className)} {...props} />
  )
})
